// Shared column definitions for fixtures and results tables
import {
    formatDate,
    formatTime,
    formatScore,
    computeDay,
    computeGrade,
    getHomeAwayIndicator,
    getResultColor
} from './formatting';
import React from 'react';
import CompetitionLink from '../components/CompetitionLink';

const cellStyle = { padding: "0.3rem 0.5rem" };
const nowrapStyle = { whiteSpace: "nowrap", padding: "0.3rem 0.5rem" };

/**
 * Creates column definitions for fixtures tables.
 * @param {boolean} includeHomeAway - Whether to include home/away indicators in venue column
 * @returns {Array} Column definitions array
 */
export function getFixtureColumns(includeHomeAway = true) {
    const columns = [
        { header: "Grade", render: item => computeGrade(item) },
        { header: "Date", render: item => formatDate(item.dateAndTime) },
        { header: "Time", render: item => formatTime(item.dateAndTime) },
        { header: "Day", render: item => computeDay(item.dateAndTime) },
        { header: "Competition", render: item => <CompetitionLink competition={item.competition} competitionUrl={item.competitionUrl} /> },
        { header: "Home", render: item => item.homeTeam || "-" },
        { header: "Away", render: item => item.awayTeam || "-" },
    ];

    if (includeHomeAway) {
        columns.push({
            header: "Venue",
            render: item => {
                const venue = item.venue || "-";
                if (venue === "-") return venue;

                const { indicator, className, title } = getHomeAwayIndicator(venue);
                return (
                    <div className="venue-container">
                        <span className={`home-away-icon ${className}`} title={title}>
                            {indicator}
                        </span>
                        <span className="team-name">{venue}</span>
                    </div>
                );
            }
        });
    } else {
        columns.push({ header: "Venue", render: item => item.venue || "-" });
    }

    columns.push({ header: "Referee", render: item => item.referee || "-" });

    return columns;
}

/**
 * Creates column definitions for results tables.
 * @param {boolean} includeHomeAway - Whether to include home/away indicators in venue column
 * @param {boolean} styleResults - Whether to style result badges with colors
 * @returns {Array} Column definitions array
 */
export function getResultColumns(includeHomeAway = true, styleResults = true) {
    const columns = [
        { header: "Grade", render: item => computeGrade(item) },
        { header: "Date", render: item => formatDate(item.dateAndTime) },
        { header: "Time", render: item => formatTime(item.dateAndTime) },
        { header: "Day", render: item => computeDay(item.dateAndTime) },
        { header: "Competition", render: item => <CompetitionLink competition={item.competition} competitionUrl={item.competitionUrl} /> },
        { header: "Home", render: item => item.homeTeam || "-" },
        { header: "Away", render: item => item.awayTeam || "-" },
        {
            header: "Score",
            render: item => formatScore(item),
            cellStyle: nowrapStyle
        },
    ];

    if (styleResults) {
        columns.push({
            header: "Result",
            render: item => {
                const result = item.result || "-";
                if (result === "-") return result;

                const colorClass = getResultColor(result);
                return (
                    <span className={colorClass}>
                        {result}
                    </span>
                );
            }
        });
    } else {
        columns.push({ header: "Result", render: item => item.result || "-" });
    }

    if (includeHomeAway) {
        columns.push({
            header: "Venue",
            render: item => {
                const venue = item.venue || "-";
                if (venue === "-") return venue;

                const { indicator, className, title } = getHomeAwayIndicator(venue);
                return (
                    <div className="venue-container">
                        <span className={`home-away-icon ${className}`} title={title}>
                            {indicator}
                        </span>
                        <span className="team-name">{venue}</span>
                    </div>
                );
            }
        });
    } else {
        columns.push({ header: "Venue", render: item => item.venue || "-" });
    }

    columns.push({ header: "Referee", render: item => item.referee || "-" });

    return columns;
}

export { cellStyle, nowrapStyle };
